/*
 * Stories viewer for sections/cp-stories.liquid. No library.
 *
 * One full-screen <dialog>, built once and reused for every circle:
 *
 *   - segmented progress on top; a photo lasts 5 s, a video its own length;
 *   - tap the left third → back, anywhere else → forward; hold → pause;
 *   - swipe down or Esc → close; ← → between frames, space → pause;
 *   - after the last frame of a circle the next circle starts, as on
 *     Instagram; after the last circle the viewer closes;
 *   - a frame with a product shows a card: tap a size → that size goes into
 *     the cart. The viewer closes first, because adding opens the cart drawer
 *     (auto_open_cart_drawer) and a drawer under a full-screen layer is
 *     invisible.
 *
 * Seen circles are remembered (localStorage, per circle id) and drawn with a
 * grey ring, so the row tells a returning visitor what is new.
 *
 * Lenis is stopped while open — otherwise the page scrolls under the layer.
 */
import { enqueue, announce, track } from '@theme/cp-ladder-ui';

const PHOTO_MS = 5000;
const SEEN_KEY = 'cp-stories-seen';

function readSeen() {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) || '[]'); } catch (e) { return []; }
}
function markSeen(id) {
  try {
    const seen = readSeen();
    if (seen.indexOf(id) === -1) {
      seen.push(id);
      localStorage.setItem(SEEN_KEY, JSON.stringify(seen.slice(-50)));
    }
  } catch (e) { /* private mode: the ring just stays coloured */ }
}

function el(tag, cls, html) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (html != null) node.innerHTML = html;
  return node;
}

function escapeHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}

const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class Viewer {
  constructor() {
    this.stories = [];
    this.si = 0;
    this.fi = 0;
    this.timer = null;
    this.started = 0;
    this.elapsed = 0;
    this.paused = false;
    this.build();
  }

  build() {
    const d = el('dialog', 'cp-sv');
    d.setAttribute('aria-label', 'Сторіс');
    d.innerHTML =
      '<div class="cp-sv__stage">' +
      '  <div class="cp-sv__bars"></div>' +
      '  <div class="cp-sv__top">' +
      '    <img class="cp-sv__avatar" alt="">' +
      '    <span class="cp-sv__title"></span>' +
      '    <span class="cp-sv__count"></span>' +
      '    <button type="button" class="cp-sv__btn cp-sv__sound" aria-label="Звук" hidden>' +
      '      <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M3 8h3l4-3v10l-4-3H3z" fill="currentColor"/><path class="cp-sv__wave" d="M13 7.5a3.5 3.5 0 0 1 0 5M15 5.5a6.5 6.5 0 0 1 0 9" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path class="cp-sv__mute" d="M13 8l4 4M17 8l-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
      '    </button>' +
      '    <button type="button" class="cp-sv__btn cp-sv__close" aria-label="Закрити">' +
      '      <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
      '    </button>' +
      '  </div>' +
      '  <div class="cp-sv__media"></div>' +
      '  <button type="button" class="cp-sv__zone cp-sv__zone--prev" aria-label="Попередній кадр"></button>' +
      '  <button type="button" class="cp-sv__zone cp-sv__zone--next" aria-label="Наступний кадр"></button>' +
      '  <div class="cp-sv__bottom"></div>' +
      '</div>' +
      '<p class="cp-sv__hint" aria-hidden="true">← → гортати · пробіл — пауза · Esc — закрити</p>';
    document.body.appendChild(d);

    this.d = d;
    this.bars = d.querySelector('.cp-sv__bars');
    this.media = d.querySelector('.cp-sv__media');
    this.bottom = d.querySelector('.cp-sv__bottom');
    this.titleEl = d.querySelector('.cp-sv__title');
    this.countEl = d.querySelector('.cp-sv__count');
    this.avatar = d.querySelector('.cp-sv__avatar');
    this.sound = d.querySelector('.cp-sv__sound');
    this.muted = true;

    d.querySelector('.cp-sv__close').addEventListener('click', () => this.close());
    this.sound.addEventListener('click', () => this.toggleSound());
    d.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });
    d.addEventListener('click', (e) => { if (e.target === d) this.close(); });

    // Tap zones: a short press moves, a long press pauses and does not move.
    const zones = d.querySelectorAll('.cp-sv__zone');
    zones.forEach((zone) => {
      let downAt = 0;
      zone.addEventListener('pointerdown', () => { downAt = Date.now(); this.pause(); });
      zone.addEventListener('pointerup', () => {
        const held = Date.now() - downAt > 250;
        this.resume();
        if (held) return;
        if (window.cpSound) window.cpSound.play('tap');
        if (zone.classList.contains('cp-sv__zone--prev')) this.prev(); else this.next();
      });
      zone.addEventListener('pointerleave', () => { if (this.paused && downAt) { downAt = 0; this.resume(); } });
      // Keyboard activation of the zone buttons.
      zone.addEventListener('click', (e) => {
        if (e.detail !== 0) return;
        if (zone.classList.contains('cp-sv__zone--prev')) this.prev(); else this.next();
      });
    });

    // Swipe down to close.
    let y0 = null;
    d.addEventListener('touchstart', (e) => { y0 = e.touches[0].clientY; }, { passive: true });
    d.addEventListener('touchend', (e) => {
      if (y0 == null) return;
      const dy = e.changedTouches[0].clientY - y0;
      y0 = null;
      if (dy > 90) this.close();
    });

    /*
     * On document, not on the dialog: showModal() does not always land focus
     * inside it here (the theme's own dialog code moves it back to body), and
     * keys sent to body would never reach a listener on the dialog.
     */
    document.addEventListener('keydown', (e) => {
      if (!this.d.open) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.prev(); }
      else if (e.key === ' ') {
        // Space on a size or link presses it; anywhere else it pauses —
        // including the close button, which holds focus when the viewer opens.
        if (e.target.closest('.cp-sv__bottom')) return;
        e.preventDefault();
        this.paused ? this.resume() : this.pause();
      }
    });

    // Sizes inside a frame.
    this.bottom.addEventListener('click', (e) => {
      const size = e.target.closest('[data-sv-size]');
      if (size) { e.preventDefault(); this.add(size); return; }
      if (e.target.closest('a')) this.close();
    });

    document.addEventListener('visibilitychange', () => {
      if (!this.d.open) return;
      document.hidden ? this.pause() : this.resume();
    });
  }

  open(stories, index) {
    this.stories = stories;
    this.si = index;
    this.fi = 0;
    if (!this.d.open && window.cpSound) window.cpSound.play('story');
    if (!this.d.open) this.d.showModal();
    this.d.querySelector('.cp-sv__close').focus({ preventScroll: true });
    document.documentElement.classList.add('cp-sv-open');
    if (window.lenis) window.lenis.stop();
    this.show();
  }

  close() {
    this.stop();
    this.media.innerHTML = '';
    if (this.d.open) this.d.close();
    document.documentElement.classList.remove('cp-sv-open');
    if (window.lenis) window.lenis.start();
    refreshRings();
    if (this.returnFocus) this.returnFocus.focus({ preventScroll: true });
  }

  get story() { return this.stories[this.si]; }
  get frame() { return this.story.frames[this.fi]; }

  show() {
    this.stop();
    const story = this.story;
    markSeen(story.id);

    // Bars
    this.bars.innerHTML = '';
    story.frames.forEach((_, i) => {
      const bar = el('span', 'cp-sv__bar' + (i < this.fi ? ' is-done' : ''));
      bar.appendChild(el('i'));
      this.bars.appendChild(bar);
    });
    this.titleEl.textContent = story.title;
    this.countEl.textContent = (this.fi + 1) + ' з ' + story.frames.length;
    if (story.cover) { this.avatar.src = story.cover; this.avatar.hidden = false; } else { this.avatar.hidden = true; }

    // Media
    const f = this.frame;
    this.media.innerHTML = '';
    this.media.classList.remove('is-fit');
    // Not 9:16 (±8 %): show it whole over its blurred self instead of
    // cropping. Reels and story photos fill the 9:16 stage.
    const fit = (w, h) => {
      if (w && h && Math.abs(w / h - 9 / 16) > 0.045) this.media.classList.add('is-fit');
    };
    let node;
    if (f.type === 'text') {
      node = el('div', 'cp-sv__text',
        '<span class="cp-sv__kicker">' + escapeHtml(f.kicker || '') + '</span>' +
        '<p class="cp-sv__big">' + escapeHtml(f.title || '') + '</p>' +
        '<p class="cp-sv__small">' + escapeHtml(f.text || '') + '</p>');
      this.sound.hidden = true;
    } else if (f.type === 'video') {
      this.media.style.setProperty('--sv-bg', f.poster ? 'url("' + f.poster + '")' : 'none');
      node = el('video', 'cp-sv__video');
      node.src = f.src;
      if (f.poster) node.poster = f.poster;
      node.playsInline = true;
      node.muted = this.muted;
      node.preload = 'auto';
      node.setAttribute('playsinline', '');
      node.addEventListener('ended', () => this.next());
      node.addEventListener('loadedmetadata', () => { this.duration = node.duration * 1000; fit(node.videoWidth, node.videoHeight); });
      this.sound.hidden = false;
    } else {
      node = el('img', 'cp-sv__img');
      node.addEventListener('load', () => fit(node.naturalWidth, node.naturalHeight));
      node.src = f.src;
      // Blurred copy behind, in case the photo turns out not to be 9:16.
      this.media.style.setProperty('--sv-bg', 'url("' + f.src + '")');
      node.alt = f.caption || f.quote || '';
      this.sound.hidden = true;
    }
    this.media.appendChild(node);
    this.node = node;
    this.preloadNext();

    // Bottom: review quote, caption, product card, or the closing CTA.
    this.bottom.innerHTML = '';
    if (f.quote) {
      const stars = '★★★★★'.slice(0, Math.max(1, Math.min(5, Number(f.stars) || 5)));
      this.bottom.appendChild(el('figure', 'cp-sv__quote',
        '<span class="cp-sv__stars" aria-label="' + (f.stars || 5) + ' з 5">' + stars + '</span>' +
        '<blockquote>' + escapeHtml(f.quote) + '</blockquote>' +
        (f.author ? '<figcaption>' + escapeHtml(f.author) + '</figcaption>' : '')));
    } else if (f.caption && !f.product) {
      this.bottom.appendChild(el('p', 'cp-sv__caption', escapeHtml(f.caption)));
    }
    if (f.product) this.bottom.appendChild(this.productCard(f.product, f.caption));
    const last = this.fi === story.frames.length - 1;
    if (last && story.cta && story.ctaUrl) {
      this.bottom.appendChild(el('a', 'cp-sv__cta', escapeHtml(story.cta) + ' ›'));
      this.bottom.lastChild.href = story.ctaUrl;
    }

    this.elapsed = 0;
    this.duration = PHOTO_MS;

    /*
     * A photo's five seconds start when it is on screen, not when it was
     * asked for — on a slow connection the first second or two would
     * otherwise run out on a black frame.
     */
    if (node.tagName === 'IMG' && !node.complete) {
      this.d.classList.add('is-loading');
      let begun = false;
      const begin = () => {
        if (begun || this.node !== node) return;
        begun = true;
        this.d.classList.remove('is-loading');
        this.start();
      };
      node.addEventListener('load', begin, { once: true });
      node.addEventListener('error', begin, { once: true });
      setTimeout(begin, 4000);
    } else {
      this.d.classList.remove('is-loading');
      this.start();
    }
    track('story_view', { story: story.title, frame: this.fi + 1 });
  }

  productCard(p, caption) {
    // "Бомбер 'STREET' дутий, ХАКІ" → name + a quiet "хакі", like the card.
    const cut = p.title.lastIndexOf(', ');
    const name = cut > 0 ? p.title.slice(0, cut) : p.title;
    const colour = cut > 0 ? p.title.slice(cut + 2).toLowerCase() : '';
    let sizes = '';
    if (p.sizes && p.sizes.length) {
      sizes = '<div class="cp-sv__sizes" role="group" aria-label="Додати розмір">' +
        p.sizes.map(function (s) {
          return s.ok && s.id
            ? '<button type="button" class="cp-sv__size" data-sv-size="' + s.id + '">' + escapeHtml(s.label) + '</button>'
            : '<button type="button" class="cp-sv__size is-out" disabled>' + escapeHtml(s.label) + '</button>';
        }).join('') + '</div>';
    } else if (p.single && p.available) {
      sizes = '<div class="cp-sv__sizes"><button type="button" class="cp-sv__size cp-sv__size--wide" data-sv-size="' + p.single + '">Додати в кошик</button></div>';
    }
    return el('div', 'cp-sv__product',
      '<a class="cp-sv__product-link" href="' + escapeHtml(p.url) + '">' +
      (p.img ? '<img src="' + escapeHtml(p.img) + '" alt="" width="48" height="60">' : '') +
      '<span class="cp-sv__product-text">' +
      (caption ? '<span class="cp-sv__product-caption">' + escapeHtml(caption) + '</span>' : '') +
      '<span class="cp-sv__product-title">' + escapeHtml(name) + '</span>' +
      (colour ? '<span class="cp-sv__product-caption">' + escapeHtml(colour) + '</span>' : '') +
      '<span class="cp-sv__product-price">' + escapeHtml(p.price) +
      (p.compare ? ' <s>' + escapeHtml(p.compare) + '</s>' : '') + '</span>' +
      '</span>' +
      '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>' +
      '</a>' + sizes);
  }

  preloadNext() {
    const next = this.story.frames[this.fi + 1];
    if (!next) return;
    if (next.type === 'image') { const i = new Image(); i.src = next.src; }
  }

  start() {
    this.paused = false;
    this.d.classList.remove('is-paused');
    const bar = this.bars.children[this.fi];
    const fill = bar && bar.firstChild;
    if (this.node.tagName === 'VIDEO') {
      const v = this.node;
      v.play().catch(() => { /* autoplay blocked: tap resumes */ });
      const tick = () => {
        if (!this.d.open || this.node !== v) return;
        if (v.duration && fill) fill.style.transform = 'scaleX(' + Math.min(1, v.currentTime / v.duration) + ')';
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
      return;
    }
    this.started = performance.now();
    const tick = (now) => {
      if (!this.d.open) return;
      const t = this.elapsed + (now - this.started);
      if (fill) fill.style.transform = 'scaleX(' + Math.min(1, t / this.duration) + ')';
      if (t >= this.duration) { this.next(); return; }
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop() {
    cancelAnimationFrame(this.raf);
    if (this.node && this.node.tagName === 'VIDEO') this.node.pause();
  }

  pause() {
    if (this.paused) return;
    this.paused = true;
    this.d.classList.add('is-paused');
    cancelAnimationFrame(this.raf);
    if (this.node && this.node.tagName === 'VIDEO') this.node.pause();
    else this.elapsed += performance.now() - this.started;
  }

  resume() {
    if (!this.paused) return;
    this.start();
  }

  next() {
    if (this.fi < this.story.frames.length - 1) { this.fi += 1; this.show(); return; }
    if (this.si < this.stories.length - 1) { this.si += 1; this.fi = 0; this.show(); return; }
    this.close();
  }

  prev() {
    if (this.fi > 0) { this.fi -= 1; this.show(); return; }
    if (this.si > 0) { this.si -= 1; this.fi = this.stories[this.si].frames.length - 1; this.show(); return; }
    this.show();
  }

  toggleSound() {
    this.muted = !this.muted;
    this.d.classList.toggle('is-unmuted', !this.muted);
    if (this.node && this.node.tagName === 'VIDEO') this.node.muted = this.muted;
  }

  add(button) {
    if (button.classList.contains('is-busy')) return;
    const id = Number(button.dataset.svSize);
    button.classList.add('is-busy');
    this.pause();
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const title = this.story.title;
    enqueue(function () {
      return fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }] }),
      })
        .then(function (r) { if (!r.ok) throw new Error('add ' + r.status); return fetch(root + 'cart.js'); })
        .then(function (r) { return r.json(); });
    })
      .then((cart) => {
        button.classList.remove('is-busy');
        button.classList.add('is-done');
        button.textContent = '✓';
        if (window.cpPlay) window.cpPlay(button, 'pop');
        // Let the tick register, then get out of the drawer's way.
        setTimeout(() => { this.close(); announce(cart, 'cp-story'); }, 450);
      })
      .catch(() => {
        button.classList.remove('is-busy');
        if (window.cpPlay) window.cpPlay(button, 'shake');
        this.resume();
      });
  }
}

let viewer = null;

function refreshRings() {
  const seen = readSeen();
  document.querySelectorAll('[data-cp-story]').forEach(function (btn) {
    btn.classList.toggle('is-seen', seen.indexOf(btn.dataset.cpStory) !== -1);
  });
}

function storiesFor(root) {
  return Array.prototype.map.call(root.querySelectorAll('[data-cp-story-data]'), function (s) {
    try { return JSON.parse(s.textContent); } catch (e) { return null; }
  }).filter(function (s) { return s && s.frames && s.frames.length; });
}

document.addEventListener('click', function (event) {
  const btn = event.target.closest('[data-cp-story]');
  if (!btn) return;
  const root = btn.closest('[data-cp-stories]');
  const stories = storiesFor(root);
  const index = stories.findIndex(function (s) { return s.id === btn.dataset.cpStory; });
  if (index === -1) return;
  if (!viewer) viewer = new Viewer();
  viewer.returnFocus = btn;
  viewer.open(stories, index);
});

refreshRings();
if (reduceMotion) document.documentElement.classList.add('cp-sv-reduce');
